import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild } from "@angular/core";
import { ChatAudit } from "@common/ADAPT.Common.Model/organisation/chat-audit";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { AiPricingService } from "@common/lib/chat-ai/ai-pricing.service";
import { ObjectUtilities } from "@common/lib/utilities/object-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DxGridWrapperHelper } from "@common/ux/base.component/dx-component-wrapper-builder";
import { DateFormats } from "@common/ux/date-formats";
import { PeopleService } from "app/features/people/people.service";
import dxDataGrid, { ContentReadyEvent, InitializedEvent } from "devextreme/ui/data_grid";
import { DxDataGridComponent, DxSelectBoxComponent } from "devextreme-angular";
import { debounceTime, lastValueFrom, Subject, tap } from "rxjs";
import { AiConversationDetailsDialogComponent, IAiConversationDetailsDialogData } from "../ai-conversation-details-dialog/ai-conversation-details-dialog.component";

type ChatAuditWithCost = ChatAudit & { totalCost: number };
type DateRangePreset =
    | "today"
    | "yesterday"
    | "thisWeek"
    | "sinceLastWeek"
    | "lastWeek"
    | "last7Days"
    | "last30Days"
    | "last90Days"
    | "thisMonth"
    | "sinceLastMonth"
    | "lastMonth"
    | "thisYear"
    | "sinceLastYear"
    | "lastYear"
    | "last12Months";

interface IDateRangePresetOption {
    text: string;
    value: DateRangePreset;
}

interface IGroupDataItem {
    organisationId?: unknown;
    items?: unknown[];
    collapsedItems?: unknown[];
}

interface IPersonGroupData {
    key: number;
    items?: unknown[];
    collapsedItems?: unknown[];
}

@Component({
    selector: "adapt-ai-usage",
    standalone: false,
    templateUrl: "./ai-usage.component.html",
    styleUrls: ["./ai-usage.component.css"],
})
export class AiUsageComponent extends BaseComponent implements OnChanges, OnInit {
    @Input() public organisation?: Organisation;
    public readonly RunAdaptName = "runAdapt";
    public readonly DateRangePresets: IDateRangePresetOption[] = [
        { text: "Today", value: "today" },
        { text: "Yesterday", value: "yesterday" },
        { text: "This week", value: "thisWeek" },
        { text: "Since last week", value: "sinceLastWeek" },
        { text: "Last week", value: "lastWeek" },
        { text: "Last 7 days", value: "last7Days" },
        { text: "Last 30 days", value: "last30Days" },
        { text: "Last 90 days", value: "last90Days" },
        { text: "This month", value: "thisMonth" },
        { text: "Since last month", value: "sinceLastMonth" },
        { text: "Last month", value: "lastMonth" },
        { text: "This year", value: "thisYear" },
        { text: "Since last year", value: "sinceLastYear" },
        { text: "Last year", value: "lastYear" },
        { text: "Last 12 months", value: "last12Months" },
    ];
    public readonly orgSelectTab = "AI Usage";
    private readonly Today = this.getToday();

    @ViewChild(DxSelectBoxComponent) private dateRangePresetSelectBox?: DxSelectBoxComponent;
    @ViewChild(DxDataGridComponent) private dataGrid?: DxDataGridComponent;

    public DateFormats = DateFormats;
    public chatAudits: ChatAuditWithCost[] = [];
    public personTotalCost: { [personId: number]: number | undefined } = {};
    public orgPersonTotalCost: { [organisationId: number]: { [personId: number]: number | undefined } | undefined } = {};
    public organisationTotalCost: { [organisationId: number]: number | undefined } = {};
    public totalCostCurrencyFormat = { type: "currency", precision: 4 };
    public dxGridWrapperHelper: DxGridWrapperHelper;
    public startDate = new Date(this.Today.getFullYear(), this.Today.getMonth() - 1, 1);
    public endDate = this.Today;
    public selectedDateRangePreset: DateRangePreset | null = null;

    // roles-page.component also uses similar dxo-header-filter pattern
    public orgNames: { text: string; value: number | null }[] = [];
    public personNames: { text: string; value: number }[] = [];

    private triggerDateRangePresetUpdate$ = new Subject<void>();
    private shouldCollapsePersonGroupsOnLoad = false;

    public constructor(
        private aiService: AiPricingService,
        private dialogService: AdaptCommonDialogService,
        private peopleService: PeopleService,
        elementRef: ElementRef,
    ) {
        super();

        this.dxGridWrapperHelper = new DxGridWrapperHelper("adapt-ai-usage", jQuery(elementRef.nativeElement));

        this.triggerDateRangePresetUpdate$.pipe(
            debounceTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.dateRangePresetSelectBox?.instance.reset());
    }

    public ngOnInit() {
        if (!this.organisation) {
            this.dxGridWrapperHelper.saveGridState("-all-");
            this.dxGridWrapperHelper.callGrid((grid) => grid.setColumnsReady());
            this.fetchChatAudits();
        }
    }

    public ngOnChanges() {
        this.dxGridWrapperHelper.saveGridState(`-${this.organisation?.organisationId ?? "all"}-`);
        // setColumnsReady first before loading so that the grid at least shows up without data and show a loading panel instead of blank panel
        this.dxGridWrapperHelper.callGrid((grid) => grid.setColumnsReady());

        if (this.organisation) {
            this.fetchChatAudits();
        }
    }

    public onGridInitialized(e: InitializedEvent) {
        this.dxGridWrapperHelper.initialiseGrid(e);
        e.component!.option().onToolbarPreparing = this.dxGridWrapperHelper.addResetToolbarButton;
    }

    public onStartDateChanged(value: Date | string | number | null) {
        const nextDate = this.toDate(value);

        if (nextDate) {
            this.startDate = nextDate;
            this.selectedDateRangePreset = null;
            this.triggerDateRangePresetUpdate$.next();
        }
    }

    public onEndDateChanged(value: Date | string | number | null) {
        const nextDate = this.toDate(value);

        if (nextDate) {
            this.endDate = nextDate;
            this.selectedDateRangePreset = null;
            this.triggerDateRangePresetUpdate$.next();
        }
    }

    public onDateRangePresetChanged(value: DateRangePreset | null) {
        this.selectedDateRangePreset = value;

        if (!value) {
            return;
        }

        const today = this.getToday();

        switch (value) {
            case "today":
                this.startDate = today;
                this.endDate = today;
                break;

            case "yesterday":
                this.startDate = this.addDays(today, -1);
                this.endDate = this.addDays(today, -1);
                break;

            case "thisWeek":
                this.startDate = this.getStartOfWeek(today);
                this.endDate = today;
                break;

            case "sinceLastWeek":
                this.startDate = this.addDays(this.getStartOfWeek(today), -7);
                this.endDate = today;
                break;

            case "lastWeek":
                this.startDate = this.addDays(this.getStartOfWeek(today), -7);
                this.endDate = this.addDays(this.getStartOfWeek(today), -1);
                break;

            case "last7Days":
                this.startDate = this.addDays(today, -6);
                this.endDate = today;
                break;

            case "last30Days":
                this.startDate = this.addDays(today, -29);
                this.endDate = today;
                break;

            case "last90Days":
                this.startDate = this.addDays(today, -89);
                this.endDate = today;
                break;

            case "thisMonth":
                this.startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                this.endDate = today;
                break;

            case "sinceLastMonth":
                this.startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                this.endDate = today;
                break;

            case "lastMonth":
                this.startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                this.endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;

            case "thisYear":
                this.startDate = new Date(today.getFullYear(), 0, 1);
                this.endDate = today;
                break;

            case "sinceLastYear":
                this.startDate = new Date(today.getFullYear() - 1, 0, 1);
                this.endDate = today;
                break;

            case "lastYear":
                this.startDate = new Date(today.getFullYear() - 1, 0, 1);
                this.endDate = new Date(today.getFullYear() - 1, 11, 31);
                break;

            case "last12Months":
                this.startDate = new Date(today.getFullYear(), today.getMonth() - 11, 1);
                this.endDate = today;
                break;
        }

        this.fetchChatAudits();
    }

    public onFetchClicked() {
        this.fetchChatAudits();
    }

    public onGridContentReady(e: ContentReadyEvent) {
        if (this.organisation || !this.shouldCollapsePersonGroupsOnLoad) {
            return;
        }

        this.collapsePeopleGroups(e.component);
        this.shouldCollapsePersonGroupsOnLoad = false;
    }

    public collapsePeopleGroups(grid?: dxDataGrid) {
        const dataGridInstance = grid ?? this.dataGrid?.instance;
        if (!dataGridInstance) {
            return;
        }

        dataGridInstance.collapseAll(1);
    }

    public expandPeopleGroups(grid?: dxDataGrid) {
        const dataGridInstance = grid ?? this.dataGrid?.instance;
        if (!dataGridInstance) {
            return;
        }

        dataGridInstance.expandAll(1);
    }

    public collapseOrganisationGroups(grid?: dxDataGrid) {
        const dataGridInstance = grid ?? this.dataGrid?.instance;
        if (!dataGridInstance) {
            return;
        }

        dataGridInstance.collapseAll(0);
    }

    public expandOrganisationGroups(grid?: dxDataGrid) {
        const dataGridInstance = grid ?? this.dataGrid?.instance;
        if (!dataGridInstance) {
            return;
        }

        dataGridInstance.expandAll(0);
    }

    private fetchChatAudits() {
        this.shouldCollapsePersonGroupsOnLoad = !this.organisation;

        this.aiService.getChatAudits({
            organisationId: this.organisation?.organisationId,
            startDate: this.startDate,
            endDate: this.endDate,
        }).pipe(
            tap((chatAudits) => {
                this.chatAudits = chatAudits.map((chatAudit) => ({
                    ...ObjectUtilities.nativeShallowClone(chatAudit), // spread operator ...chatAudit as it is breeze entity
                    totalCost: this.aiService.calculateTotalCost(chatAudit),
                }));
                this.updateTotalCostMaps();
            }),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public getPersonGroupTotal(groupData: IPersonGroupData) {
        if (this.organisation) {
            return this.personTotalCost[groupData.key] ?? 0;
        }

        const organisationId = this.getOrganisationIdFromGroupItems(groupData.items ?? groupData.collapsedItems);

        return organisationId == null
            ? 0
            : this.orgPersonTotalCost[organisationId]?.[groupData.key] ?? 0;
    }

    public async showConversationDetails(chatAudit: ChatAudit) {
        const userName = chatAudit.person?.fullName
            ?? (await lastValueFrom(this.peopleService.getPersonById(chatAudit.personId)))?.fullName
            ?? "Unknown user";
        this.dialogService.open(AiConversationDetailsDialogComponent, {
            conversationId: chatAudit.conversationId,
            userName,
        } as IAiConversationDetailsDialogData).pipe(
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private updateTotalCostMaps() {
        this.personTotalCost = {};
        this.orgPersonTotalCost = {};
        this.organisationTotalCost = {};
        this.orgNames = [];
        this.personNames = [];
        for (const chatAudit of this.chatAudits) {
            if (!this.personNames.some((person) => person.value === chatAudit.personId)) {
                this.personNames.push({
                    text: chatAudit.person?.fullName ?? `Person ${chatAudit.personId}`,
                    value: chatAudit.personId,
                });
            }

            if (this.organisation) {
                this.personTotalCost[chatAudit.personId] = (this.personTotalCost[chatAudit.personId] ?? 0) + chatAudit.totalCost;
                continue;
            }

            const organisationId = chatAudit.organisationId ?? 0;
            this.organisationTotalCost[organisationId] = (this.organisationTotalCost[organisationId] ?? 0) + chatAudit.totalCost;
            const organisationPersonTotals = this.orgPersonTotalCost[organisationId] ??= {};
            organisationPersonTotals[chatAudit.personId] = (organisationPersonTotals[chatAudit.personId] ?? 0) + chatAudit.totalCost;
            if (!this.orgNames.some((org) => org.value === chatAudit.organisationId)) {
                this.orgNames.push({
                    text: chatAudit.organisation?.name ?? this.RunAdaptName,
                    value: chatAudit.organisationId ?? null,
                });
            }
        }

        this.personNames.sort((a, b) => a.text.localeCompare(b.text));

        // will only show organisation column if not locked to an organisation
        if (!this.organisation) {
            // sort orgNames by name but put RunAdaptName at the top
            this.orgNames.sort((a, b) => {
                if (a.text === this.RunAdaptName) {
                    return -1;
                }

                if (b.text === this.RunAdaptName) {
                    return 1;
                }

                return a.text.localeCompare(b.text);
            });
        }
    }

    private getOrganisationIdFromGroupItems(items?: unknown[]): number | null {
        const firstItem = items?.[0];

        if (!firstItem || typeof firstItem !== "object") {
            return null;
        }

        const groupItem = firstItem as IGroupDataItem;

        if ("organisationId" in groupItem) {
            return typeof groupItem.organisationId === "number" ? groupItem.organisationId : 0;
        }

        const nestedItems = groupItem.items ?? groupItem.collapsedItems;

        return Array.isArray(nestedItems)
            ? this.getOrganisationIdFromGroupItems(nestedItems)
            : null;
    }

    private addDays(date: Date, numberOfDays: number) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + numberOfDays);
    }

    private getStartOfWeek(date: Date) {
        const dayOfWeek = date.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

        return this.addDays(date, -daysFromMonday);
    }

    private getToday() {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    private toDate(value: Date | string | number | null) {
        if (value == null) {
            return null;
        }

        const date = value instanceof Date ? value : new Date(value);

        return Number.isNaN(date.getTime()) ? null : date;
    }
}
